import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { ClavaContext } from "../../../config/contexts";
import { AppDispatch, RootState } from "../../../store";
import { makeSelectLeagueMatchDaysById, selectMatchDays, selectSportsType } from "../../../selectors";
import { fetchMatchDays, fetchMatchDaysOfLeague } from "../../../store/actions/matchActions";
import {
  formatRelativeDateLabel,
  formatYmdLocal,
  isFiniteDate,
  parseYmdLocal,
  toLocalMidnight
} from "../../../config/utils";
import { pathHomeDate, pathHomeLeagueDate, toYmd } from "../../../config/routes";
import DateNavigator from "../DateNavigator";

const EMPTY_DATES: ReadonlyArray<Date> = Object.freeze([]);
const EMPTY_YMD_LIST: ReadonlyArray<string> = Object.freeze([]);

type UrlContext = {
  currentLeagueId?: string;
  currentDateYmd?: string;
};

type SelectorFn = (state: RootState) => ReadonlyArray<Date>;

const DateSelector: React.FC = () => {
  const { l, aoi } = useContext(ClavaContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const params = useParams();
  const { currentLeagueId, currentDateYmd } = useMemo<UrlContext>(
    () => ({ currentLeagueId: params.leagueId, currentDateYmd: params.date }),
    [params.leagueId, params.date]
  );

  // no-op

  const selectedDate = useMemo(() => {
    if (currentDateYmd && /^\d{4}-\d{2}-\d{2}$/.test(currentDateYmd)) {
      const [year, month, day] = currentDateYmd
        .split("-")
        .map((n) => parseInt(n, 10));
      const parsedDate = new Date(year, month - 1, day);
      if (isFiniteDate(parsedDate)) return toLocalMidnight(parsedDate);
    }
    return toLocalMidnight(new Date());
  }, [currentDateYmd]);

  const selectedYmd = useMemo(() => formatYmdLocal(selectedDate), [selectedDate]);

  // no-op

  const matchDaysRaw = useSelector(
    useMemo<SelectorFn>(() => {
      if (currentLeagueId) {
        const lid = parseInt(currentLeagueId, 10);
        return Number.isNaN(lid)
          ? () => EMPTY_DATES
          : makeSelectLeagueMatchDaysById(lid);
      }
      return selectMatchDays;
    }, [currentLeagueId])
  );

  const matchDays = useMemo(
    () =>
      [...matchDaysRaw]
        .map((d) => toLocalMidnight(new Date(d)))
        .filter((d) => isFiniteDate(d))
        .sort((a, b) => a.getTime() - b.getTime()),
    [matchDaysRaw]
  );

  const visibleDaysYmd = useMemo(() => {
    if (!matchDays || matchDays.length === 0) return EMPTY_YMD_LIST;
    const set = new Set<string>();
    matchDays.forEach((d) => set.add(formatYmdLocal(d)));
    return Array.from(set.values()).sort();
  }, [matchDays]);

  const visibleDaysYmdSet = useMemo(() => new Set(visibleDaysYmd), [visibleDaysYmd]);
  const isActiveYmd = useCallback((d: Date) => visibleDaysYmdSet.has(formatYmdLocal(d)), [visibleDaysYmdSet]);

  // no-op

  const sports = useSelector(selectSportsType);

  const [pending, setPending] = useState<
    | undefined
    | {
    dir: "next" | "prev";
    kind: "league" | "aoi";
    lid?: number;
    aoi?: number;
    anchorYmd: string;
  }
  >(undefined);
  const redirectInProgressRef = useRef<boolean>(false);
  const lastRedirectTargetRef = useRef<string | null>(null);
  const lastLoadedMonthKeyRef = useRef<string | null>(null);
  const lastHandledPath = useRef<string | null>(null);
  const requestedInitialDaysRef = useRef<boolean>(false);

  useEffect(() => {
    if (currentLeagueId) {
      const lid = parseInt(currentLeagueId, 10);
      const hasEntry = (matchDays?.length ?? 0) > 0;
      if (!Number.isNaN(lid) && !hasEntry) {
        fetchMatchDaysOfLeague(dispatch, lid, new Date(), "bigger", sports);
      }
    }
  }, [currentLeagueId, dispatch, sports, matchDays?.length]);

  const label = useMemo(
    () => formatRelativeDateLabel(selectedDate, l),
    [selectedDate, l]
  );

  const goTo = useCallback((d: Date) => {
    const ymdDash = toYmd(d);
    if (currentLeagueId) {
      navigate(pathHomeLeagueDate(parseInt(currentLeagueId, 10), ymdDash));
    } else {
      navigate(pathHomeDate(ymdDash));
    }
  }, [currentLeagueId, navigate]);

  const onDayPress = useCallback(
    (date: Date) => {
      if (date && isActiveYmd(date)) {
        goTo(date);
      }
    },
    [isActiveYmd, goTo]
  );

  useEffect(() => {
    lastLoadedMonthKeyRef.current = null;
    requestedInitialDaysRef.current = false;
    redirectInProgressRef.current = false;
    lastRedirectTargetRef.current = null;
    lastHandledPath.current = null;
  }, [currentLeagueId, aoi, sports]);

  const onMonthChange = useCallback(
    (activeStartDate: Date) => {
      const year = activeStartDate.getFullYear();
      const month = activeStartDate.getMonth() + 1;
      const loadMonth = (yy: number, mm: number) => {
        const monthKey = `${currentLeagueId ? `L${currentLeagueId}` : `A${aoi}`}#${yy}-${mm}#S${sports}`;
        if (lastLoadedMonthKeyRef.current === monthKey) return;
        lastLoadedMonthKeyRef.current = monthKey;
        const monthStart = new Date(yy, mm - 1, 1);
        if (currentLeagueId) {
          const lid = parseInt(currentLeagueId, 10);
          if (!Number.isNaN(lid)) {
            fetchMatchDaysOfLeague(dispatch, lid, monthStart, "month", sports);
          }
        } else {
          fetchMatchDays(dispatch, aoi, monthStart, "month", sports);
        }
      };
      loadMonth(year, month);
    },
    [aoi, currentLeagueId, dispatch, sports]
  );

  useEffect(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth() + 1;
    const monthKey = `${currentLeagueId ? `L${currentLeagueId}` : `A${aoi}`}#${year}-${month}#S${sports}`;
    if (lastLoadedMonthKeyRef.current === monthKey) return;
    lastLoadedMonthKeyRef.current = monthKey;
    const monthStart = new Date(year, month - 1, 1);
    if (currentLeagueId) {
      const lid = parseInt(currentLeagueId, 10);
      if (!Number.isNaN(lid)) {
        fetchMatchDaysOfLeague(dispatch, lid, monthStart, "month", sports);
      }
    } else {
      fetchMatchDays(dispatch, aoi, monthStart, "month", sports);
    }
  }, [selectedDate, currentLeagueId, aoi, sports, dispatch]);

  const pickClosestTarget = useCallback((): string | null => {
    if (visibleDaysYmd.length === 0) return null;
    const next = visibleDaysYmd.find((d) => d >= selectedYmd);
    const prev = [...visibleDaysYmd].reverse().find((d) => d <= selectedYmd);
    return next || prev || null;
  }, [visibleDaysYmd, selectedYmd]);

  const redirectIfNeeded = useCallback((target: string | null, path: string) => {
    if (!target || target === selectedYmd) return false;
    if (lastRedirectTargetRef.current === target && redirectInProgressRef.current) return false;
    lastHandledPath.current = path;
    lastRedirectTargetRef.current = target;
    redirectInProgressRef.current = true;
    const dt = parseYmdLocal(target) ?? toLocalMidnight(new Date());
    goTo(dt);
    return true;
  }, [goTo, selectedYmd]);

  useEffect(() => {
    if (!currentDateYmd || !/^\d{4}-\d{2}-\d{2}$/.test(currentDateYmd)) return;
    if (visibleDaysYmd.length === 0) return;
    if (visibleDaysYmd.includes(selectedYmd)) return;

    const first = visibleDaysYmd[0];
    const last = visibleDaysYmd[visibleDaysYmd.length - 1];
    const base = parseYmdLocal(selectedYmd) ?? toLocalMidnight(new Date());
    if (selectedYmd > last) {
      // Need future days
      if (currentLeagueId) {
        const lid = parseInt(currentLeagueId, 10);
        if (!Number.isNaN(lid)) {
          fetchMatchDaysOfLeague(dispatch, lid, base, "bigger", sports);
        }
      } else {
        fetchMatchDays(dispatch, aoi, base, "bigger", sports);
      }
    } else if (selectedYmd < first) {
      // Need past days
      if (currentLeagueId) {
        const lid = parseInt(currentLeagueId, 10);
        if (!Number.isNaN(lid)) {
          fetchMatchDaysOfLeague(dispatch, lid, base, "smaller", sports);
        }
      } else {
        fetchMatchDays(dispatch, aoi, base, "smaller", sports);
      }
    }
  }, [currentDateYmd, selectedYmd, visibleDaysYmd, currentLeagueId, sports, dispatch, aoi]);

  useEffect(() => {
    const path = location.pathname;
    if (lastHandledPath.current === path) return;
    const hasSelected = visibleDaysYmd.includes(selectedYmd);
    if (hasSelected) return;

    const isOnHomePath = path.startsWith("/home");
    if (isOnHomePath) return;

    const isOnSpecificDate =
      /^\/home\/date\/\d{4}-\d{2}-\d{2}$/.test(path) ||
      (currentLeagueId && new RegExp(`^/home/league/${currentLeagueId}/date/\\d{4}-\\d{2}-\\d{2}$`).test(path));
    if (isOnSpecificDate) return;

    const currentTodayYmd = formatYmdLocal(toLocalMidnight(new Date()));
    if (selectedYmd === currentTodayYmd && !hasSelected) {
      if (visibleDaysYmd.length > 0) {
        const target = pickClosestTarget();
        if (redirectIfNeeded(target, path)) return;
      }
    }

    if (visibleDaysYmd.length > 0) {
      const target = pickClosestTarget();
      if (redirectIfNeeded(target, path)) return;
    }
    if (pending || requestedInitialDaysRef.current) return;
    requestedInitialDaysRef.current = true;
    const base = toLocalMidnight(new Date());
    if (currentLeagueId) {
      const lid = parseInt(currentLeagueId, 10);
      if (!Number.isNaN(lid)) {
        setPending({
          dir: "next",
          kind: "league",
          lid,
          anchorYmd: selectedYmd
        });
        fetchMatchDaysOfLeague(dispatch, lid, base, "today", sports);
      }
    } else {
      setPending({ dir: "next", kind: "aoi", aoi, anchorYmd: selectedYmd });
      fetchMatchDays(dispatch, aoi, base, "today", sports);
    }
  }, [
    visibleDaysYmd,
    selectedYmd,
    currentLeagueId,
    navigate,
    aoi,
    dispatch,
    sports,
    pending,
    location.pathname,
    pickClosestTarget,
    redirectIfNeeded
  ]);

  useEffect(() => {
    if (visibleDaysYmd.includes(selectedYmd)) {
      redirectInProgressRef.current = false;
      lastRedirectTargetRef.current = null;
      lastHandledPath.current = null;
      requestedInitialDaysRef.current = false;
    }
  }, [visibleDaysYmd, selectedYmd]);

  const onPrev = () => {
    if (visibleDaysYmd.length > 0) {
      const prev = [...visibleDaysYmd].reverse().find((d) => d < selectedYmd);
      if (prev) {
        const dt = parseYmdLocal(prev) ?? toLocalMidnight(new Date());
        goTo(dt);
        return;
      }
    }
    const anchorYmd = visibleDaysYmd.length ? visibleDaysYmd[0] : selectedYmd;
    const base = parseYmdLocal(anchorYmd) ?? toLocalMidnight(new Date());
    if (currentLeagueId) {
      const lid = parseInt(currentLeagueId, 10);
      if (!Number.isNaN(lid)) {
        setPending({ dir: "prev", kind: "league", lid, anchorYmd });
        fetchMatchDaysOfLeague(dispatch, lid, base, "smaller", sports);
      }
    } else {
      setPending({ dir: "prev", kind: "aoi", aoi, anchorYmd });
      fetchMatchDays(dispatch, aoi, base, "smaller", sports);
    }
  };

  const onNext = () => {
    if (visibleDaysYmd.length > 0) {
      const next = visibleDaysYmd.find((d) => d > selectedYmd);
      if (next) {
        const dt = parseYmdLocal(next) ?? toLocalMidnight(new Date());
        goTo(dt);
        return;
      }
    }
    const anchorYmd = visibleDaysYmd.length ? visibleDaysYmd[visibleDaysYmd.length - 1] : selectedYmd;
    const base = parseYmdLocal(anchorYmd) ?? toLocalMidnight(new Date());
    if (currentLeagueId) {
      const lid = parseInt(currentLeagueId, 10);
      if (!Number.isNaN(lid)) {
        setPending({ dir: "next", kind: "league", lid, anchorYmd });
        fetchMatchDaysOfLeague(dispatch, lid, base, "bigger", sports);
      }
    } else {
      setPending({ dir: "next", kind: "aoi", aoi, anchorYmd });
      fetchMatchDays(dispatch, aoi, base, "bigger", sports);
    }
  };

  return (
    <DateNavigator
      selectedDate={selectedDate}
      availableDates={matchDays}
      label={label}
      onPrev={onPrev}
      onNext={onNext}
      onDateSelect={onDayPress}
      onMonthChange={onMonthChange}
    />
  );
};

export default DateSelector;
